import { POStatusTypes } from "@/api_framework/api_modals/Inventory";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { MutableRefObject, useEffect, useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { BiSave } from "react-icons/bi";
import { useNavigate } from "react-router";
import { IInventoryPurchaseOrderFormFields } from "..";
import SendToFinanceModal from "../../SendToFinanceModal";
import FinanceCreditNoteUploadDocumentsModal from "../../FinanceCreditNoteUploadModal";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { IOcrVerificationGate } from "../ProductInfoFormFields";
import OcrBulkDecisionModal, { OcrBulkDecision } from "../ProductInfoFormFields/OcrBulkDecisionModal";

interface IPOSubmitButtonProps {
  POProductsHook: UseFieldArrayReturn<IInventoryPurchaseOrderFormFields, "product_info", "id">;
  onSubmit: (overrideNextPOStatus?: POStatusTypes) => void;
  formHook: UseFormReturn<IInventoryPurchaseOrderFormFields>;
  attachementFieldHook: UseFieldArrayReturn<
    IInventoryPurchaseOrderFormFields,
    "attachments" | "product_info" | "invoice_details.attachments",
    "id"
  >;
  setPDFView: (value: boolean) => void;
  ocrGateRef?: MutableRefObject<IOcrVerificationGate | null>;
  isInvoiceUploading?: boolean;
}
const POSubmitButton: React.FC<IPOSubmitButtonProps> = ({
  attachementFieldHook,
  onSubmit,
  POProductsHook,
  formHook,
  setPDFView,
  ocrGateRef,
  isInvoiceUploading = false,
}) => {
  const { getRouteKey } = useRouter();
  const { setVisible } = useModalActions("PO_RAISE_ERROR_MODAL");
  const userPermissions = useAppSelector((state) => state.auth?.userPermissions);
  const { purchaseOrderDetails: PODetails } = useRouteInfo(getRouteKey("INVENTORY", "id"))?.routeState
    ?.state as IInventoryState;
  const invoiceDetails = formHook?.watch("invoice_details");
  const isInvoiceFormFilled =
    !!invoiceDetails?.invoice_date && !!invoiceDetails?.goods_receiving_date && !!invoiceDetails?.invoice_number;
  const navigate = useNavigate();
  const [initialProductIds, setInitialProductIds] = useState<string[]>([]);
  const [isProductChanged, setIsProductChanged] = useState(false);
  const [editPermission, setEditPermission] = useState<boolean>(false);
  const [isFinanceModalOpen, setFinanceModalOpen] = useState(false);
  const [isFinanceUploadModalOpen, setFinanceUploadModalOpen] = useState(false);
  const [financeReason, setFinanceReason] = useState<string | null>(null);
  // Open For GRN stops here while the scanned invoice still has undecided differences.
  const [isOcrGateOpen, setOcrGateOpen] = useState(false);
  const [isResolvingOcrGate, setResolvingOcrGate] = useState(false);
  const isCentralOps = useFeatureAccess("PO_CREATE_ACTION");
  const canHandleIssueInGrn = useFeatureAccess("PO_ISSUE_IN_GRN_CLINIC_ACTION");

  useEffect(() => {
    if (!PODetails || !userPermissions) return;
    if (isCentralOps) {
      setEditPermission(true);
    } else {
      setEditPermission(false);
    }
  }, [userPermissions, PODetails]);

  const handleActionButton = (nextStatus?: POStatusTypes) => {
    if (nextStatus) {
      onSubmit(nextStatus);
    } else {
      onSubmit();
    }
  };

  useEffect(() => {
    if (initialProductIds.length === 0 && PODetails?.product_info?.length) {
      setInitialProductIds(PODetails.product_info.map((p: any) => p.variant_id));
    }
  }, [PODetails]);

  useEffect(() => {
    const currentIds = POProductsHook.fields.map((p) => p.variant_id);
    const changed =
      currentIds.length !== initialProductIds.length ||
      currentIds.some((id) => !initialProductIds.includes(id)) ||
      initialProductIds.some((id) => !currentIds.includes(id));
    setIsProductChanged(changed);
  }, [POProductsHook.fields, initialProductIds]);

  return (
    <div className="bottom-0 flex justify-end items-center w-full right-0">
      {editPermission && PODetails?.po_status === "pending_invoice_upload" && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleActionButton(PODetails?.po_status);
          }}
          type="button"
          disabled={isInvoiceUploading}
          className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
        >
          <BiSave className="w-4 h-4" />
          <span>Save</span>
        </button>
      )}
      {PODetails?.po_status === "awaiting_grn" && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isCentralOps) {
              if (isProductChanged) {
                handleActionButton(PODetails?.po_status);
              } else {
                navigate(`/app/inventory/pending-goods?po_id=${PODetails?.po_id}`);
              }
            } else {
              navigate(`/app/inventory/pending-goods?po_id=${PODetails?.po_id}`);
            }
          }}
          className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
        >
          {isCentralOps
            ? isProductChanged
              ? "Open For GRN"
              : "View GRN"
            : "GRN Against PO"}
        </button>
      )}
      {editPermission && PODetails.po_status === "pending_invoice_verification" && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Undecided invoice differences have to be settled before the PO can move to GRN.
            if (ocrGateRef?.current?.hasPendingChanges) {
              setOcrGateOpen(true);
              return;
            }
            handleActionButton();
          }}
          type="button"
          disabled={isInvoiceUploading}
          className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
        >
          <span>Open For GRN</span>
        </button>
      )}
      {editPermission && (PODetails?.po_status === "pre_draft" || PODetails?.po_status === "draft") && (
        <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 p-3 bg-white">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleActionButton("draft");
            }}
            type="button"
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200"
          >
            Save Draft
          </button>
          <button
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const isDeliveryDateValid = await formHook.trigger("primary_purchase_info.delivery_date");
              if (!isDeliveryDateValid) return;
              setPDFView(true);
            }}
            disabled={POProductsHook?.fields?.length === 0}
            type="button"
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
          >
            Save & Preview PDF
          </button>
        </div>
      )}
      {!editPermission &&
        (PODetails?.po_status === "pending_invoice_upload" || PODetails?.po_status === "issue_in_invoice_upload") && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleActionButton();
            }}
            disabled={isInvoiceUploading || attachementFieldHook.fields.length === 0 || !isInvoiceFormFilled}
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
          >
            Send to Central
          </button>
        )}
      {PODetails?.po_status === "closed" && isCentralOps && (
        <div className="flex justify-end items-center gap-x-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setVisible();
            }}
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#FFF1F4] border border-[#F9C7D4] text-[#E11D48] hover:bg-[#FFE7EE]"
          >
            Raise An Error
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (PODetails?.open_modal_for_finance_note && !financeReason) {
                setFinanceModalOpen(true);
              } else if (PODetails?.open_modal_for_credit_note) {
                setFinanceUploadModalOpen(true);
              } else {
                if (financeReason) {
                  formHook.setValue("pending_finance_approval_note", financeReason);
                }
                handleActionButton("sent_to_finance");
              }
            }}
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
          >
            Send To Finance
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/app/inventory/pending-goods?po_id=${PODetails?.po_id}`);
            }}
            className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
          >
            View GRN
          </button>
        </div>
      )}
      {PODetails?.po_status === "issue_in_grn" && canHandleIssueInGrn && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/app/inventory/pending-goods?po_id=${PODetails?.po_id}`);
          }}
          className="inline-flex justify-center items-center gap-1.5 h-8 px-4 rounded-lg font-medium text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black text-white hover:opacity-90"
        >
          Resolve GRN Issues
        </button>
      )}
      <OcrBulkDecisionModal
        variant={isOcrGateOpen ? "gate" : null}
        changes={ocrGateRef?.current?.changeSummary("accept") ?? []}
        isSubmitting={isResolvingOcrGate}
        onConfirm={async (decision: OcrBulkDecision) => {
          const gate = ocrGateRef?.current;
          if (!gate) return;
          setResolvingOcrGate(true);
          try {
            await gate.applyBulkDecision(decision);
          } finally {
            setResolvingOcrGate(false);
          }
          setOcrGateOpen(false);
          handleActionButton();
        }}
        onGoBack={() => !isResolvingOcrGate && setOcrGateOpen(false)}
      />
      {isFinanceModalOpen && (
        <SendToFinanceModal
          open={isFinanceModalOpen}
          onSave={(reason: string) => {
            setFinanceReason(reason);
            formHook.setValue("pending_finance_approval_note", reason);
            handleActionButton("sent_to_finance");
            setFinanceModalOpen(false);
          }}
          setOpen={setFinanceModalOpen}
        />
      )}
      {isFinanceUploadModalOpen && (
        <FinanceCreditNoteUploadDocumentsModal
          open={isFinanceUploadModalOpen}
          onSave={(creditNoteImage: string) => {
            formHook.setValue("credit_note_attachment", creditNoteImage);
            setFinanceUploadModalOpen(false);
            handleActionButton("sent_to_finance");
          }}
          setOpen={setFinanceUploadModalOpen}
        />
      )}
    </div>
  );
};

export default POSubmitButton;
